import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { STAGES, STAGE_LABEL, fmtINR, fmtDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, HandCoins } from "lucide-react";
import MultiSelectProducts from "@/components/MultiSelectProducts";
import { MultiSelectServices, MultiSelectTrainings } from "@/components/MultiSelectCatalog";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import ManufacturerFilter from "@/components/ManufacturerFilter";
import { useAuth } from "@/contexts/AuthContext";

export function NewDealDialog({ open, onOpenChange, onCreated, pipelineType: initialPipelineType = "product" }) {
  // Pipeline selector — drives which kanban the new card lands in. Defaults
  // to the pipeline the user opened the dialog from (or "product" when
  // launched standalone from the Deals page). Training has its own first
  // stage ("enquiry"); product & service both start at "qualification".
  const [pipelineType, setPipelineType] = useState(initialPipelineType);
  const firstStage = pipelineType === "training" ? "enquiry" : "qualification";
  // `service_ids` covers BOTH Technical Services and Trainings — the
  // services collection holds both flavours, distinguished by `type`.
  // We send a single field to the backend regardless of pipeline so the
  // existing /deals POST API keeps working unchanged.
  const [form, setForm] = useState({ title: "", value: "", stage: firstStage, client_id: "", next_followup_date: "", next_action: "", notes: "", product_ids: [], service_ids: [], training_start_date: "", training_end_date: "" });
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoTitle, setAutoTitle] = useState(true);  // suppresses overwrite once user manually edits
  const { user } = useAuth();

  useEffect(() => {
    if (open) {
      api.get("/clients").then((r) => setClients(r.data));
      api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
      api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
      setPipelineType(initialPipelineType);
      setForm((f) => ({ ...f, stage: initialPipelineType === "training" ? "enquiry" : "qualification" }));
      setAutoTitle(true);
    }
  }, [open, initialPipelineType]);

  // Keep the locked-stage display in sync if the user switches pipeline
  // inside the dialog without closing it. Also clear the opposite picker
  // so a Service deal doesn't accidentally carry product_ids from when the
  // pipeline was "product" earlier.
  useEffect(() => {
    setForm((f) => ({
      ...f,
      stage: firstStage,
      product_ids: pipelineType === "product" ? f.product_ids : [],
      service_ids: pipelineType !== "product" ? f.service_ids : [],
    }));
  }, [firstStage, pipelineType]);

  // Auto-build deal name "[Subject] - [Salesperson] - [Company]" whenever
  // the first linked product/service or client changes, but only while the
  // title hasn't been manually edited. For service / training pipelines
  // the Subject is the FIRST SERVICE NAME (e.g. "Electrical PD Inspection")
  // — previously this only looked at products which is why service deals
  // came out titled "Rajashekar Krishnan - Kalyani Steels Ltd" with no
  // service name in front.
  useEffect(() => {
    if (!open || !autoTitle) return;
    const company = clients.find((c) => c.id === form.client_id)?.company_name;
    let subject = "";
    if (pipelineType === "product") {
      const firstProductId = (form.product_ids || [])[0];
      subject = products.find((p) => p.id === firstProductId)?.name
        || (form.product_ids?.length ? "Product" : "");
    } else {
      const firstServiceId = (form.service_ids || [])[0];
      subject = services.find((s) => s.id === firstServiceId)?.name
        || (form.service_ids?.length ? (pipelineType === "training" ? "Training" : "Service") : "");
    }
    const parts = [subject, user?.name, company].filter(Boolean);
    if (parts.length >= 2) {
      setForm((f) => ({ ...f, title: parts.join(" - ") }));
    }
  }, [open, autoTitle, form.client_id, form.product_ids, form.service_ids, pipelineType, clients, products, services, user]);

  const submit = async () => {
    if (!form.title || !form.client_id) { toast.error("Title and client required"); return; }
    if (pipelineType === "training" && form.training_start_date && form.training_end_date) {
      if (form.training_end_date < form.training_start_date) {
        toast.error("Training end date can't be before the start date");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        stage: firstStage,
        pipeline_type: pipelineType,
        value: Number(form.value) || 0,
        product_ids: form.product_ids || [],
        service_ids: form.service_ids || [],
        next_followup_date: form.next_followup_date ? new Date(form.next_followup_date + "T12:00:00").toISOString() : null,
        // Training-pipeline-only: send the YYYY-MM-DD strings only when on
        // the Training pipeline so we don't silently stamp them onto
        // product/service deals.
        training_start_date: pipelineType === "training" ? (form.training_start_date || null) : null,
        training_end_date: pipelineType === "training" ? (form.training_end_date || null) : null,
      };
      await api.post("/deals", payload);
      toast.success(`Deal created in ${pipelineType.charAt(0).toUpperCase() + pipelineType.slice(1)} pipeline`);
      onCreated?.();
      onOpenChange(false);
      setForm({ title: "", value: "", stage: firstStage, client_id: "", next_followup_date: "", next_action: "", notes: "", product_ids: [], service_ids: [], training_start_date: "", training_end_date: "" });
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">New deal</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {/* Pipeline picker — drives which kanban the new card lands in and which stage list applies. */}
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Pipeline *</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="deal-pipeline-type">
              {[
                { v: "product",  l: "Product",  d: "Equipment sale" },
                { v: "service",  l: "Service",  d: "On-site / analysis" },
                { v: "training", l: "Training", d: "Workshop / course" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setPipelineType(opt.v)}
                  data-testid={`deal-pipeline-type-${opt.v}`}
                  className={`p-2.5 rounded-2xl border text-left tap transition ${
                    pipelineType === opt.v
                      ? "border-primary bg-primary-50 ring-2 ring-primary/30"
                      : "border-border/60 hover:bg-muted/30"
                  }`}
                >
                  <p className="font-semibold text-sm">{opt.l}</p>
                  <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{opt.d}</p>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">Card will appear in the selected pipeline's first stage.</p>
          </div>
          <div className="col-span-2"><Label className="text-xs font-semibold">Title *</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.title} onChange={(e) => { setAutoTitle(false); setForm({ ...form, title: e.target.value }); }} data-testid="deal-title-input" placeholder="Pick products + client below — auto-generated, editable" />
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug">Auto-fills as &ldquo;Product - Salesperson - Company&rdquo;. Edit freely.</p>
          </div>
          <div className="col-span-2"><Label className="text-xs font-semibold">Client *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="deal-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent className="rounded-2xl">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-semibold">Value (₹)</Label><Input className="rounded-2xl h-11 mt-1.5" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="700000" /></div>
          <div>
            <Label className="text-xs font-semibold">Starts in</Label>
            <div className="rounded-2xl h-11 mt-1.5 px-3 flex items-center bg-muted/60 border border-border/60 text-sm font-semibold text-muted-foreground" data-testid="deal-stage-locked">
              {firstStage === "enquiry" ? "Enquiry" : "Qualification"}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">All new deals begin at the first pipeline stage. Advance them through the pipeline as they progress.</p>
          </div>
          <div><Label className="text-xs font-semibold">Next follow-up</Label><Input type="date" className="rounded-2xl h-11 mt-1.5" value={form.next_followup_date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold">Next action</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} placeholder="Send proposal" /></div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">
              {pipelineType === "training" ? "Trainings" : pipelineType === "service" ? "Technical services" : "Products"}
            </Label>
            {pipelineType === "product" && (
              <MultiSelectProducts
                value={form.product_ids}
                onChange={(ids) => setForm({ ...form, product_ids: ids })}
                testId="deal-products-select"
                placeholder="Select one or more products…"
              />
            )}
            {pipelineType === "service" && (
              <MultiSelectServices
                value={form.service_ids}
                onChange={(ids) => setForm({ ...form, service_ids: ids })}
                testId="deal-services-select"
              />
            )}
            {pipelineType === "training" && (
              <MultiSelectTrainings
                value={form.service_ids}
                onChange={(ids) => setForm({ ...form, service_ids: ids })}
                testId="deal-trainings-select"
              />
            )}
            <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">
              {pipelineType === "training"
                ? "Pulled from the Training catalogue. Add new trainings under Products → Services tab."
                : pipelineType === "service"
                ? "Pulled from the Technical Services catalogue. Add new services under Products → Services tab."
                : "Pulled from the Product catalogue."}
            </p>
          </div>
          {/* Training-only date window. Lives outside the multi-select grid
              cell so it spans the full dialog width without disturbing the
              product / service modals. */}
          {pipelineType === "training" && (
            <>
              <div>
                <Label className="text-xs font-semibold">Training starts</Label>
                <Input
                  type="date"
                  className="rounded-2xl h-11 mt-1.5"
                  value={form.training_start_date}
                  onChange={(e) => setForm({ ...form, training_start_date: e.target.value })}
                  data-testid="deal-training-start"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Training ends</Label>
                <Input
                  type="date"
                  className="rounded-2xl h-11 mt-1.5"
                  value={form.training_end_date}
                  min={form.training_start_date || undefined}
                  onChange={(e) => setForm({ ...form, training_end_date: e.target.value })}
                  data-testid="deal-training-end"
                />
              </div>
            </>
          )}
          <div className="col-span-2"><Label className="text-xs font-semibold">Notes</Label><Textarea className="rounded-2xl mt-1.5" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="deal-save-button">{saving ? "Saving…" : "Create deal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Deals() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [stage, setStage] = useState("");
  const [q, setQ] = useState("");
  const [manufacturerId, setManufacturerId] = useState("");
  const [open, setOpen] = useState(params.get("new") === "1");

  const load = useCallback(async () => {
    const { data } = await api.get("/deals", {
      params: {
        stage: stage || undefined,
        q: q || undefined,
        manufacturer_id: manufacturerId || undefined,
      },
    });
    setRows(data);
  }, [stage, q, manufacturerId]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1280px] mx-auto">
      {pullIndicator}
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Deals</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">All opportunities.</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-2xl tap" data-testid="new-deal-button"><Plus className="h-4 w-4 mr-1" /> New deal</Button>
      </header>
      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title…" className="pl-10 h-12 rounded-2xl bg-muted/40" data-testid="deals-search-input" />
        </div>
        <Select value={stage || "__all"} onValueChange={(v) => setStage(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-12 w-48" data-testid="deals-stage-filter"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All stages</SelectItem>
            {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <ManufacturerFilter value={manufacturerId} onChange={setManufacturerId} testId="deals-supplier-filter" className="h-12 w-48 rounded-2xl" />
      </div>
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-2 sm:p-3 space-y-2">
          {rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No deals.</p>
          ) : rows.map((d) => {
            const fu = d.next_followup_date ? new Date(d.next_followup_date) : null;
            const today = new Date(); today.setHours(0,0,0,0);
            const fuDay = fu ? new Date(fu.getFullYear(), fu.getMonth(), fu.getDate()) : null;
            const fuTone = !fuDay ? "text-muted-foreground"
              : fuDay < today ? "text-red-700 font-semibold"
              : fuDay.getTime() === today.getTime() ? "text-amber-700 font-semibold"
              : "text-emerald-700";
            return (
              <Link
                key={d.id}
                to={`/deals/${d.id}`}
                className="block p-4 sm:p-5 rounded-2xl border border-border/60 hover:bg-muted/30 hover:border-primary/40 tap transition"
                data-testid={`deal-row-${d.id}`}
              >
                {/* Row 1 — icon + title/company + value pinned right */}
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
                    <HandCoins className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[15px] leading-snug line-clamp-2 break-words">{d.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {d.client_name || "Unlinked"}
                      {d.owner_name ? <span className="mx-1">·</span> : null}
                      {d.owner_name || ""}
                    </p>
                  </div>
                  <span className="font-display font-extrabold text-primary-700 text-lg whitespace-nowrap shrink-0">{fmtINR(d.value)}</span>
                </div>
                {/* Row 2 — stage pill + follow-up */}
                <div className="mt-3 pl-14 flex items-center justify-between gap-3">
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider capitalize px-2.5 py-0.5">
                    {STAGE_LABEL[d.stage] || d.stage}
                  </Badge>
                  <span className={`text-[11px] whitespace-nowrap ${fuTone}`}>
                    {fu ? fmtDate(d.next_followup_date) : "No follow-up"}
                  </span>
                </div>
              </Link>
            );
          })}
        </CardContent>
      </Card>
      <NewDealDialog open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}
